import { useEffect } from "react";
import { Printer } from "lucide-react";

import { Logo } from "./Logo";
import { site } from "../data/site";

/**
 * Turns the page it is mounted on into a printable brochure.
 *
 * "Download the brochure" prints the page you are already reading, rather
 * than opening a separately-authored PDF. That is deliberate: the previous
 * Agent Labista brochure was its own 1,300-line document with its own copy,
 * and it drifted — a sixty-day case study at a named builder, a pipeline
 * figure, tiered pricing and "twelve builders signed", none of it true once
 * the product turned out to have no users. A second document is a second
 * set of claims to keep honest, and this one was not kept honest.
 *
 * Three pieces, because each belongs at a different point in the page:
 *
 *   PrintDocFrame  first child  — sets the <html> flag the print stylesheet
 *                                 keys off, and draws the letterhead
 *   PrintButton    after the hero — the on-screen affordance
 *   PrintColophon  last child   — contact details and the status caveat
 *
 * They were one component to begin with, which put the letterhead wherever
 * that component happened to sit: the foot of page one.
 */

/** Place FIRST in the page. Print-only; renders nothing on screen. */
export function PrintDocFrame({
  docTitle,
  subtitle,
}: {
  docTitle: string;
  subtitle: string;
}) {
  useEffect(() => {
    const el = document.documentElement;
    el.dataset.printDoc = "true";
    return () => {
      delete el.dataset.printDoc;
    };
  }, []);

  return (
    <div className="print-only" aria-hidden>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12pt",
          borderBottom: "2px solid #0e6b4f",
          paddingBottom: "8pt",
          marginBottom: "12pt",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "7pt" }}>
          <Logo className="h-6 w-6" id="asta-mark-print" />
          <span style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
            {site.legalName}
          </span>
        </span>
        <span style={{ textAlign: "right", fontSize: "8pt", lineHeight: 1.4 }}>
          <strong style={{ display: "block" }}>{docTitle}</strong>
          <span>{subtitle}</span>
        </span>
      </div>
    </div>
  );
}

/** The on-screen button. Hidden from the printed page. */
export function PrintButton() {
  return (
    <div className="no-print container -mt-4 mb-2 flex justify-start sm:justify-end">
      <button
        type="button"
        onClick={() => window.print()}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:border-white/30 hover:text-white"
      >
        <Printer className="size-4" />
        Download the brochure
        <span className="text-white/40">PDF</span>
      </button>
    </div>
  );
}

/** Place LAST in the page. Print-only. */
export function PrintColophon() {
  // A long date, because an undated brochure found in a drawer is worse
  // than no brochure.
  const issued = new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="print-only" aria-hidden>
      <div
        style={{
          borderTop: "1px solid #d6dcd9",
          marginTop: "14pt",
          paddingTop: "8pt",
          fontSize: "8pt",
          lineHeight: 1.5,
        }}
      >
        <div>
          <strong>{site.legalName}</strong> · {site.locations.join(" · ")} ·{" "}
          {site.email} · astalabista.com/labista
        </div>
        <div style={{ marginTop: "2pt" }}>
          Issued {issued}. Agent Labista is in development and not yet open to
          customers; nothing in this document is a commitment to a price, a date
          or a service level. Talk to us about a pilot.
        </div>
      </div>
    </div>
  );
}
